import React, { useContext } from 'react';
import { useTranslations } from '@vp/om-translations';
import { TaxAwarePriceRow } from '@vp/component-library';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import { Box, Divider } from '@vp/swan';
import PropTypes from 'prop-types';
import useSiteData from '../../../hooks/useSiteData';
import { currencyLocaleFormatter } from '../../../util/currencyFormatter';
import { getItemGroupTotalLabel } from '../../../util/itemGroupDisplayLookup';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import {
  DEFAULT_TAX_DISPLAY,
  ITEM_GROUP_LEVEL,
} from '../../../constants/taxDisplayOptions';

/**
 * Responsible for rendering an itemGroup total
 * @param {string} groupId - groupId for the items
 * @param {object} order - order containing itemGroups
 * 1. Gets the itemGroup pricing data from context
 * 2. Generates the tax aware itemGroupTotal price for a given groupId
 * 3. Displays the itemGroupTotal
 *
 * Notes:
 * - Expected to only be rendered on orderDetailsPage to access OrderDetailsContext
 * - uses om-tax-display library to apply tax display rules (tax-inclusivity and display formatting).
 */
export const ItemGroupPricingSummary = ({ order, groupId }) => {
  const [lineItemMessages] = useTranslations(['lineItemMessages']);
  const { uiLocalization, locale } = useSiteData();
  const {
    itemGroupIdToTaxInclusiveAndExclusivePricesMap,
    currency,
    fractionDigits,
  } = useContext(OrderDetailsContext);
  const currencyLocale = currencyLocaleFormatter(
    uiLocalization && uiLocalization.locale
  );

  const { itemGroups } = order;
  const groupData = itemGroups.find(group => group.groupId === groupId);

  // get taxAware itemGroupTotal by summing all item component prices for a given itemGroup
  const taxAwareItemGroupTotal = aggregateTaxAwarePrices(
    itemGroupIdToTaxInclusiveAndExclusivePricesMap[groupId],
    ITEM_GROUP_LEVEL,
    DEFAULT_TAX_DISPLAY,
    [],
    {} // OrderDetails doesn't respect the VAT display toggle, so we pass an empty object for taxDisplayContext
  );

  // `!!` converts the Object to a Boolean
  const displayGroupPrice = !!(
    taxAwareItemGroupTotal.taxAwareListCents &&
    taxAwareItemGroupTotal.taxAwareDiscountedCents
  );

  const itemGroupTotalLabel = getItemGroupTotalLabel(groupData, locale);

  if (displayGroupPrice) {
    return (
      <Box pt={6}>
        <Divider my={4} />
        <TaxAwarePriceRow
          rowLabel={itemGroupTotalLabel || lineItemMessages.groupTotal}
          labelFormat={{
            fontSize: 3,
            textColor: 'grey-700',
            fontWeight: 'bold',
          }}
          fractionDigits={fractionDigits}
          listCents={taxAwareItemGroupTotal.taxAwareListCents}
          discountedCents={taxAwareItemGroupTotal.taxAwareDiscountedCents}
          currencyCode={currency}
          locale={currencyLocale}
          taxes={[
            ...taxAwareItemGroupTotal.includedTaxes,
            ...taxAwareItemGroupTotal.excludedTaxes,
          ]}
        />
      </Box>
    );
  }

  return null;
};

ItemGroupPricingSummary.propTypes = {
  groupId: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
};
